/**
 * Whole-chart evaluation from scratch (W1-05a engine-eval; ARCHITECTURE.md §7.1).
 *
 * Compiles every line of a lowered chart in one pass, in Phira's load order:
 *   tempo (BPMList in list order) -> bezier registry (scan order) -> max_time -> per line: tracks, controls,
 *   floor curve (legacy or 170 integrator, sub-mode from META's effective RPEVersion) -> father order.
 * The incremental engine (W1-05b) assembles the same pieces per line with its dirty DAG; this entry point is what
 * its dev "verify" mode recompiles to diff against, what the analysis worker uses for a one-shot check, and what
 * goldens and fixtures run through.
 *
 * Internal API:
 *   compileChartEval(chart, { speedMode, rpeVersionEffective?, lineId?, gifTotalMs? }) -> ChartEval
 *   chartEval.lineStates(t, ar, filters?) / lineState(index, t, ar, filters?) / floorAt(index, t) / control(...)
 */

import type { IrChart, IrLine } from '../ir/types';
import type { LineId, SpeedMode } from '../model/types';
import { createTempoMap } from '../time/tempo';
import type { Sec, TempoMap } from '../time/types';
import type { EvalEnv } from './anim';
import { buildBezierRegistry, type BezierRegistry } from './bezierRegistry';
import { LineScene, type FatherOrder } from './compose';
import type { ControlWhich } from './controls';
import { compileLineEval, type LineEval, type LineStateBuf, type SpeedSettings } from './lineState';
import { combineMaxTime, lineMaxTime } from './maxTime';
import type { PreviewFilters } from './types';

export interface ChartEvalOptions {
  /** pkg.speedMode. */
  readonly speedMode: SpeedMode;
  /** Overrides `META.rpeVersionEffective` (what-if previews of the 170 sub-mode). */
  readonly rpeVersionEffective?: number;
  /** Line id per export index (default: `srcLine`, else the index). */
  readonly lineId?: (index: number, ir: IrLine) => LineId;
  /** GIF loop duration of a GIF line's texture in ms (null = unknown). */
  readonly gifTotalMs?: (index: number, ir: IrLine) => number | null;
}

export interface ChartEval {
  readonly tempo: TempoMap;
  readonly env: EvalEnv;
  readonly registry: BezierRegistry;
  readonly speed: SpeedSettings;
  /** Phira max_time (+1 s included). */
  readonly maxTime: Sec;
  /** Each line's contribution to max_time (-Infinity when none). */
  readonly lineMaxTimes: readonly number[];
  readonly lines: readonly LineEval[];
  readonly scene: LineScene;
  readonly order: FatherOrder;
  /** States of all lines at t in export order (pooled). */
  lineStates(t: Sec, ar: number, filters?: PreviewFilters): readonly LineStateBuf[];
  /** State of one line at t (pooled). */
  lineState(index: number, t: Sec, ar: number, filters?: PreviewFilters): LineStateBuf;
  /** Floor position H(t) of a line. */
  floorAt(index: number, t: Sec): number;
  /** Note control factor of a line at key h (null = absent or inert). */
  control(index: number, which: ControlWhich, h: number): number | null;
}

/** Phira's tempo map of a lowered chart (BPMList in list order). */
export function tempoOfChart(chart: Pick<IrChart, 'BPMList'>): TempoMap {
  return createTempoMap(chart.BPMList.map((b) => ({ bpm: b.bpm, startTime: b.startTime })));
}

export function compileChartEval(chart: IrChart, opts: ChartEvalOptions): ChartEval {
  const tempo = tempoOfChart(chart);
  const lines = chart.judgeLineList;
  const registry = buildBezierRegistry(lines);
  const env: EvalEnv = { sec: (t) => tempo.secAtTriple(t), bezier: registry };
  const lineMaxTimes = lines.map((l) => lineMaxTime(l, env.sec));
  const maxTime = combineMaxTime(lineMaxTimes);
  const speed: SpeedSettings = {
    mode: opts.speedMode,
    rpeVersionEffective: opts.rpeVersionEffective ?? chart.META.rpeVersionEffective,
  };
  const evals = lines.map((ir, index) => {
    const lineId = opts.lineId?.(index, ir);
    const gif = opts.gifTotalMs?.(index, ir) ?? null;
    const le = compileLineEval(ir, env, {
      index,
      gifTotalMs: gif,
      ...(lineId === undefined ? {} : { line: lineId }),
    });
    le.setFloor(maxTime, speed);
    return le;
  });
  const scene = new LineScene(evals);
  const order = scene.order();
  const lineAt = (index: number): LineEval => {
    const le = evals[index];
    if (le === undefined) throw new RangeError(`ChartEval: no line at index ${String(index)}`);
    return le;
  };
  return {
    tempo,
    env,
    registry,
    speed,
    maxTime,
    lineMaxTimes,
    lines: evals,
    scene,
    order,
    lineStates: (t, ar, filters) => scene.evalAll(t, ar, filters),
    lineState: (index, t, ar, filters) => scene.evalLine(index, t, ar, filters),
    floorAt: (index, t) => lineAt(index).floor.heightAt(t),
    control: (index, which, h) => lineAt(index).controls.at(which, h),
  };
}
